import type { Instance, Props } from "tippy.js"
import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"

import type { CloseLoopPlaceholder } from "@productlane/lib/src/close-loop-template"
import { closeLoopPlaceholderToken } from "@productlane/lib/src/close-loop-template"

import type {
	PlaceholderKeyDownHandler,
	PlaceholderListProps,
} from "./placeholder-list"
import type { SuggestionOptions } from "@tiptap/suggestion"
import { suggestionPopupTarget } from "../suggestion-popup-target"
import PlaceholderList from "./placeholder-list"

type PlaceholderRenderer = NonNullable<
	SuggestionOptions<CloseLoopPlaceholder, { key: string }>["render"]
>

export const createPlaceholderRenderer =
	(formatToken: (key: string) => string): PlaceholderRenderer =>
	() => {
		let component: ReactRenderer<unknown, PlaceholderListProps> | null = null
		let popup: Instance<Props>[] | null = null
		let onKeyDownHandler: PlaceholderKeyDownHandler = () => false
		const setOnKeyDownHandler = (handler: PlaceholderKeyDownHandler) => {
			onKeyDownHandler = handler
		}
		let dismissed = false

		const syncVisibility = (items: CloseLoopPlaceholder[]) => {
			if (dismissed || items.length === 0) {
				popup?.[0]?.hide()
				return
			}
			popup?.[0]?.show()
		}

		return {
			onStart: (props) => {
				component = new ReactRenderer(PlaceholderList, {
					props: { ...props, setOnKeyDownHandler, formatToken },
					editor: props.editor,
				})
				if (!props.clientRect) return

				// @ts-expect-error tippy types are wrong
				popup = tippy("body", {
					getReferenceClientRect: props.clientRect,
					appendTo: () => suggestionPopupTarget(props.editor),
					content: component.element,
					showOnCreate: true,
					interactive: true,
					trigger: "manual",
					placement: "bottom-start",
					zIndex: 9999,
					popperOptions: { strategy: "fixed" },
				})
				syncVisibility(props.items)
			},

			onUpdate(props) {
				component?.updateProps({ ...props, setOnKeyDownHandler, formatToken })
				const { clientRect } = props
				if (clientRect) {
					popup?.[0]?.setProps({
						getReferenceClientRect: () => clientRect() ?? new DOMRect(),
					})
				}
				syncVisibility(props.items)
			},

			onKeyDown(props) {
				if (props.event.key === "Escape") {
					dismissed = true
					popup?.[0]?.hide()
					return true
				}
				return onKeyDownHandler(props)
			},

			onExit() {
				popup?.[0]?.destroy()
				component?.destroy()
				popup = null
				component = null
				dismissed = false
			},
		}
	}

export const closeLoopPlaceholderRenderer = createPlaceholderRenderer(
	closeLoopPlaceholderToken,
)
